import {
  Form,
  Input,
  Button,
  Card,
  Space,
  DatePicker,
  Table,
  Divider,
  Modal,
  Tag,
  Typography,
  Select,
} from 'antd'
import moment from 'moment'
import { SearchOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons'
import useFormAndRoute from '../../@hooks/useFormAndRoute'
import UserService from '../../@services/user'
import { useCallback, useEffect, useState } from 'react'
import useCreateUserDialog from './hooks/useCreateDialog'
import useModifierUserDialog from './hooks/useModifierDialog'
import useDetailUserDialog from './hooks/useDetailDialog'
import useModifierPasswordDialog from './hooks/useModifierPasswordDialog'
import AuthComponent, { isAuth } from '../../components/AuthComponent'

const DefaultFormData = {
  username: '',
  contact: '',
  status: undefined,
}

const userStatusMap = {
  0: { label: '锁定', value: 0, colorType: 'danger' },
  1: { label: '正常', value: 1, colorType: 'success' },
  2: { label: '删除', value: 2, colorType: 'secondary' },
}

const ButtonLinkStyle = { padding: 0, color: '#1890FF' }

export default function UserList() {
  const onFormDataChanged = useCallback(FormData => {
    console.log('onFormDataChanged', FormData)
  }, [])

  const { form } = useFormAndRoute(DefaultFormData, onFormDataChanged)

  const [pager, setPager] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: total => `共${total}条数据`,
  })

  const onReset = () => {
    form.resetFields()
    // form.submit()
  }

  const [updateList, setUpdateList] = useState(false)
  const openCreateUserDialog = useCreateUserDialog(() => {
    setUpdateList(val => !val)
  })

  const openModifierUserDialog = useModifierUserDialog(() => {
    setUpdateList(val => !val)
  })

  const deleteUser = async user => {
    Modal.confirm({
      title: '是否继续',
      content: `是否确认删除"${user.name}"的账号吗?`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        await UserService.delete(user.id)
        setUpdateList(val => !val)
      },
    })
  }

  const changeStatusUser = async user => {
    const handlerText = user.status === 1 ? '锁定' : '解锁'
    Modal.confirm({
      title: '是否继续',
      content: `是否确认${handlerText}"${user.name}"的账号吗?`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        await UserService.changeStatus({
          id: user.id,
          status: user.status === 1 ? 0 : 1,
        })
        setUpdateList(val => !val)
      },
    })
  }

  const openDetailUserDialog = useDetailUserDialog()
  const openModifierPasswordDialog = useModifierPasswordDialog()
  const [tableProps, setTableProps] = useState({
    loading: false,
    dataSource: [],
    rowKey: 'id',
    columns: [],
  })

  const hasTableOperation = isAuth([
    '/auth/user/changePassword',
    '/auth/user/changeStatus',
    '/auth/user/delete/{id}',
    '/auth/user/info/{id}',
    '/auth/user/update',
  ])

  useEffect(() => {
    const columns = [
      {
        title: '编号',
        dataIndex: 'id',
        width: 60,
      },
      {
        title: '账号',
        width: 220,
        render: row => {
          const userDoms = [
            <span style={{ paddingRight: '5px' }} key="username">
              {row.username}
            </span>,
          ]
          if (row.type === 1) {
            userDoms.push(
              <Tag color="error" key="admin">
                超级管理员
              </Tag>
            )
          }
          return userDoms
        },
      },
      {
        title: '初始密码',
        width: 180,
        dataIndex: 'initPassword',
      },
      {
        title: '姓名',
        width: 100,
        dataIndex: 'name',
      },
      {
        title: '关联角色',
        dataIndex: 'erpRoleVOS',
        render: roles =>
          (roles || []).map(item => (
            <Tag color={'success'} key={item.id}>
              {item.name}
            </Tag>
          )),
      },
      {
        title: '账户状态',
        dataIndex: 'status',
        width: 90,
        render: status => {
          const userStatus = userStatusMap[status] || {}
          return (
            <Typography.Text type={userStatus.colorType}>
              {userStatus.label}
            </Typography.Text>
          )
        },
      },
      {
        title: '备注',
        dataIndex: 'remark',
      },
      {
        title: '创建时间',
        width: 180,
        dataIndex: 'createdAt',
        render: date =>
          date ? moment(date).format('YYYY-MM-DD hh:mm:ss') : '',
      },
    ]

    if (hasTableOperation) {
      columns.push({
        title: '操作',
        width: 300,
        render: row => {
          const btns = []
          if (isAuth('/auth/user/update')) {
            btns.push(
              <Button
                type="link"
                key={'edit'}
                style={ButtonLinkStyle}
                onClick={() => openModifierUserDialog(row.id)}
              >
                编辑
              </Button>
            )
          }

          if (isAuth('/auth/user/info/{id}')) {
            btns.push(
              <Button
                type="link"
                key={'detail'}
                style={ButtonLinkStyle}
                onClick={() => openDetailUserDialog(row.id)}
              >
                查看
              </Button>
            )
          }

          if (isAuth('/auth/user/delete/{id}')) {
            btns.push(
              <Button
                type="link"
                key={'delete'}
                style={ButtonLinkStyle}
                onClick={() => deleteUser(row)}
              >
                删除
              </Button>
            )
          }

          if (isAuth('/auth/user/changeStatus')) {
            btns.push(
              <Button
                type="link"
                key={'lock'}
                style={ButtonLinkStyle}
                onClick={() => changeStatusUser(row)}
              >
                {row.status === 1 && '锁定'}
                {row.status === 0 && '解锁'}
              </Button>
            )
          }

          if (isAuth('/auth/user/changePassword')) {
            btns.push(
              <Button
                type="link"
                key={'reset'}
                style={ButtonLinkStyle}
                onClick={() => openModifierPasswordDialog(row)}
              >
                重置密码
              </Button>
            )
          }

          return btns.reduce((pre, item, index) => {
            if (index !== 0) {
              pre.push(<Divider type="vertical" key={`Divider-${index}`} />)
            }
            pre.push(item)
            return pre
          }, [])
        },
      })
    }
    setTableProps(state => ({ ...state, columns }))
  }, [hasTableOperation])

  const [searchParams, setSearchParams] = useState({})

  const search = () => {
    const req = form.getFieldsValue() || {}
    req.startTime = undefined
    req.endTime = undefined
    if (Array.isArray(req.dateRange) && req.dateRange.length === 2) {
      req.startTime = moment(req.dateRange[0]._d)
        .startOf('day')
        .format('YYYY-MM-DD HH:mm:ss')
      req.endTime = moment(req.dateRange[1]._d)
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss')
    }

    if (req.dateRange) {
      delete req.dateRange
    }
    setSearchParams(req)
    setPager(state => ({ ...state, page: 1 }))
  }

  useEffect(() => {
    const getData = async () => {
      try {
        setTableProps(state => ({ ...state, loading: true }))
        const res =
          (await UserService.getList({
            ...searchParams,
            pageIndex: pager.page,
            pageSize: pager.pageSize,
          })) || {}

        setPager(state => ({ ...state, total: res.recordCount || 0 }))
        setTableProps(state => ({
          ...state,
          dataSource: res.result || [],
        }))
      } catch (error) {
        console.log({ error })
      } finally {
        setTableProps(state => ({ ...state, loading: false }))
      }
    }
    getData()
  }, [pager.pageSize, pager.page, searchParams, updateList])

  const onChange = page => {
    setPager(state => ({
      ...state,
      page: page.current,
      pageSize: page.pageSize,
    }))
  }

  return (
    <Space direction="vertical" style={{ width: '100%', textAlign: 'left' }}>
      <Card>
        <Form form={form} layout="inline" onFinish={search} onReset={onReset}>
          <Form.Item name="username" label="账号">
            <Input style={{ width: 150 }} placeholder="请输入内容" />
          </Form.Item>
          <Form.Item name="dateRange" label="创建时间">
            <DatePicker.RangePicker></DatePicker.RangePicker>
          </Form.Item>
          <Form.Item name="status" label="账号状态">
            <Select
              options={[userStatusMap[0], userStatusMap[1]]}
              style={{ width: '100px' }}
              allowClear
            />
          </Form.Item>
          <Form.Item>
            <Button
              htmlType="submit"
              icon={<SearchOutlined />}
              ghost
              type="primary"
            >
              搜索
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              htmlType="button"
              icon={<ReloadOutlined />}
              onClick={onReset}
              ghost
              type="primary"
            >
              重置
            </Button>
          </Form.Item>
          <AuthComponent authKey={'/auth/user/create'}>
            <Form.Item>
              <Button
                type="primary"
                htmlType="button"
                icon={<PlusOutlined />}
                onClick={openCreateUserDialog}
              >
                添加
              </Button>
            </Form.Item>
          </AuthComponent>
        </Form>
      </Card>
      <Card>
        <Table {...tableProps} pagination={pager} onChange={onChange} />
      </Card>
    </Space>
  )
}
